<template>
  <auth-form-wrapper
    title="SAML Test Login (Internal)"
    subtitle="⚠️ TEMPORARY TEST ENDPOINT - REMOVE BEFORE PRODUCTION ⚠️"
    :error="error"
    :community="communityData"
  >
    <!-- Warning Banner -->
    <b-alert variant="warning" show class="mb-3">
      <strong>⚠️ SECURITY WARNING:</strong> This is a temporary test endpoint with NO authentication. 
      It will log in ANY email you provide. <strong>MUST BE REMOVED BEFORE PRODUCTION!</strong>
    </b-alert>

    <!-- Use native form submission to handle 302 redirects properly -->
    <form 
      ref="testForm"
      :action="formAction"
      method="POST"
      class="auth-form mt-2"
      @submit="handleFormSubmit"
      autocomplete="off"
    >
      <!-- Email -->
      <div class="mb-1">
        <label class="form-label" for="test-email">
          Email
        </label>
        <input
          id="test-email"
          v-model="formData.email"
          type="email"
          name="email"
          class="form-control"
          placeholder="e.g., andrmari@bancolombia.com.co"
          required
        />
      </div>

      <!-- Community Slug -->
      <div class="mb-1">
        <label class="form-label" for="test-community-slug">
          Community Slug
        </label>
        <input
          id="test-community-slug"
          v-model="formData.communitySlug"
          type="text"
          name="communitySlug"
          class="form-control"
          placeholder="e.g., nexos"
          required
        />
      </div>

      <!-- Submit Button -->
      <div class="auth-btn-container d-flex justify-content-center">
        <button
          type="submit"
          class="btn btn-primary auth-btn auth-btn-primary mt-2 mb-2"
          :disabled="isSubmitting"
        >
          {{ isSubmitting ? 'Testing Login...' : 'Test SAML Login' }}
        </button>
      </div>
    </form>

    <!-- Info Section -->
    <div class="mt-3 p-3 bg-light rounded">
      <h6 class="mb-2">How it works:</h6>
      <ul class="text-left small">
        <li>Bypasses Azure AD authentication</li>
        <li>Backend sets httpOnly cookies (frontendToken, backendToken)</li>
        <li>Backend returns user and session info in response</li>
        <li>Frontend stores user/session info and redirects to main app</li>
        <li>All API calls use httpOnly cookies automatically (no Bearer tokens needed)</li>
      </ul>
    </div>
  </auth-form-wrapper>
</template>

<script>
import Vue from 'vue'
import AuthFormWrapper from '@/auth/components/AuthFormWrapper.vue'
import { authHandler } from '@/auth/mixins/auth-handler'
import { getDomain } from '@/store/auth/auth.store'
import { AUTH_STATE_COOKIE } from '@/store/auth/auth.store.constants'

export default {
  name: 'SAMLTestLogin',

  inject: {
    authApiUrl: {
      default: process.env.VUE_APP_AUTH_API_URL || 'https://auth-api.nectios.com/auth'
    },
    community: {
      default: () => null
    }
  },

  mixins: [authHandler],

  components: {
    AuthFormWrapper
  },

  data() {
    return {
      formData: {
        email: '',
        communitySlug: ''
      },
      error: null,
      isSubmitting: false,
      communityData: null
    }
  },

  computed: {
    communitySlug() {
      // Try to get from route params first, then from form
      const slugParam = this.$route.params.communityId;
      if (slugParam && slugParam !== 'undefined' && slugParam !== 'null') {
        return slugParam;
      }
      return this.formData.communitySlug || '';
    },
    formAction() {
      // Build the endpoint URL for the form action
      // The endpoint is at /v1/auth/saml/test-login
      const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname.includes('localhost');
      
      if (isDevelopment) {
        // Use proxy path in development (configured in vue.config.js)
        return '/v1/auth/saml/test-login';
      } else {
        // Use full URL in production
        const baseUrl = process.env.VUE_APP_AUTH_API_URL || 'https://auth-api.nectios.com';
        return `${baseUrl}/v1/auth/saml/test-login`;
      }
    }
  },

  async created() {
    // Pre-fill community slug from route if available
    if (this.$route.params.communityId) {
      this.formData.communitySlug = this.$route.params.communityId;
    }

    // Load community data if slug is available
    if (this.communitySlug) {
      await this.loadCommunityData();
    }
  },

  methods: {
    async loadCommunityData() {
      try {
        // This is similar to how Login.vue loads community data
        // You might want to implement this if needed for theming
        this.communityData = this.community ? this.community() : null;
      } catch (error) {
        console.error('Error loading community data:', error);
      }
    },

    handleFormSubmit(event) {
      // Validate form before submission
      if (!this.formData.email || !this.communitySlug) {
        event.preventDefault();
        this.error = 'Please provide both email and community slug';
        return false;
      }

      // Clear any previous errors
      this.error = null;
      this.isSubmitting = true;

      // Request storage access for cross-site cookies if needed
      if (document.hasStorageAccess && typeof document.requestStorageAccess === 'function') {
        document.requestStorageAccess().catch(storageError => {
          console.warn('Storage access request failed:', storageError);
          // Continue anyway - the form will still submit
        });
      }

      console.log('Submitting form to:', this.formAction);
      console.log('With data:', {
        email: this.formData.email,
        communitySlug: this.communitySlug
      });

      // IMPORTANT: For security and proper cookie handling, we need to:
      // 1. Submit as JSON to the endpoint
      // 2. Let the backend set httpOnly cookies
      // 3. Backend redirects to /{communitySlug}/auth/saml-callback
      // 4. Callback page verifies cookies and completes authentication
      
      // Since the endpoint expects JSON, we'll use fetch but handle the redirect properly
      event.preventDefault();
      this.submitWithJsonAndRedirect();
      return false;
    },

    async submitWithJsonAndRedirect() {
      try {
        const endpoint = this.formAction;
        
        // First, try to get the redirect URL by following redirects
        // This will work if CORS allows it
        let redirectUrl = null;
        let token = null;
        
        try {
          const response = await fetch(endpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              email: this.formData.email,
              communitySlug: this.formData.communitySlug
            }),
            credentials: 'include',
            redirect: 'follow' // Try to follow redirects first
          });

          // If we got redirected, the response.url will be the final URL
          if (response.redirected && response.url) {
            redirectUrl = response.url;
            console.log('Redirect URL obtained:', redirectUrl);
            // Extract token from URL if present
            const urlObj = new URL(redirectUrl);
            token = urlObj.searchParams.get('token');
          }
        } catch (followError) {
          console.log('Could not follow redirect (CORS issue), trying manual redirect handling...', followError);
        }

        // If we don't have the redirect URL, try manual redirect handling
        if (!redirectUrl) {
          const response = await fetch(endpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              email: this.formData.email,
              communitySlug: this.formData.communitySlug
            }),
            credentials: 'include',
            redirect: 'manual' // Don't follow redirect automatically - we'll handle it
          });

          console.log('Response status:', response.status);
          console.log('Response type:', response.type);

          // Handle redirect responses (302, 301, 307, 308)
          if (response.status >= 300 && response.status < 400) {
            const location = response.headers.get('location');
            if (location) {
              redirectUrl = location;
              console.log('Redirect location found:', redirectUrl);
              // Extract token from URL if present
              try {
                const urlObj = new URL(location, window.location.origin);
                token = urlObj.searchParams.get('token');
              } catch (urlError) {
                console.warn('Could not parse redirect URL:', urlError);
              }
            }
          }

          // Handle opaque redirect (CORS issue, but redirect happened)
          if (response.status === 0 || response.type === 'opaqueredirect') {
            console.log('Opaque redirect detected - endpoint redirected but CORS blocked the response');
            console.log('Cookies should be set. Redirecting to SAML callback page...');
            
            // Backend has set httpOnly cookies and redirected
            // Navigate to the SAML callback page which will verify the cookies
            const callbackUrl = `/${this.communitySlug}/auth/saml-callback`;
            console.log('Navigating to SAML callback page:', callbackUrl);
            // Wait a moment for cookies to be processed by the browser
            await new Promise(resolve => setTimeout(resolve, 100));
            window.location.href = callbackUrl;
            return;
          }

          // Handle successful responses (200 OK) - backend set httpOnly cookies
          if (response.ok) {
            const contentType = response.headers.get('content-type') || '';
            if (contentType.includes('application/json')) {
              const data = await response.json();
              console.log('Response data:', data);
              
              // If we get { "message": "OK" } with user/session info, backend has set httpOnly cookies
              // We just need to store the user/session info and redirect
              if (data.message === 'OK') {
                console.log('Backend returned OK - httpOnly cookies are set');
                console.log('User info:', data.user);
                console.log('Session info:', data.sessionInfo);
                
                // Store user and session info in Vuex
                if (data.user) {
                  this.$store.commit('user-data/setLoggedUser', {
                    user: data.user,
                    member: data.member || {},
                    roles: data.roles || []
                  });
                }
                
                if (data.sessionInfo) {
                  this.$store.dispatch('session/updateSessionInfo', data.sessionInfo);
                }
                
                // Set auth state cookie (non-httpOnly, just to indicate authenticated state)
                Vue.$cookies.set(AUTH_STATE_COOKIE, 'authenticated', {
                  httpOnly: false,
                  domain: getDomain(),
                  path: '/',
                  secure: true,
                  sameSite: 'Lax'
                });
                
                // Push Nexos dataLayer if needed
                if (data.user) {
                  this.pushNexosDataLayer(data, this.$store);
                }
                
                // Wait a moment for cookies to be processed by the browser
                await new Promise(resolve => setTimeout(resolve, 100));
                
                // Redirect to main app - httpOnly cookies will be sent automatically with all requests
                console.log('Redirecting to main app...');
                await this.handleRedirect();
                return;
              }
              
              // If response has redirect URL, use it
              if (data.redirectUrl || data.url) {
                redirectUrl = data.redirectUrl || data.url;
              }
            }
          }
        }

        // If we have a redirect URL, navigate to it
        if (redirectUrl) {
          console.log('Navigating to redirect URL:', redirectUrl);
          window.location.href = redirectUrl;
          return;
        }

        // Fallback: redirect to main app
        console.log('No redirect URL found, redirecting to main app...');
        await this.handleRedirect();
      } catch (error) {
        console.error('Error submitting form:', error);
        this.error = error.message || 'Test login failed. Please check the console for details.';
        this.isSubmitting = false;
      }
    },

  }
};
</script>

<style lang="scss" scoped>
.auth-form {
  width: 100%;
}

.form-label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
}

.bg-light {
  background-color: #f8f9fa !important;
}

.text-left {
  text-align: left;
}

.small {
  font-size: 0.875rem;
}
</style>

